/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Content,
  Header,
  InfoCard,
  Link,
  Page,
  Progress,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import UndoIcon from '@material-ui/icons/Undo';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import { HowItWorks, HowItWorksStep } from '../common/HowItWorks';
import { OrgRail, OrgSearchSummary, orgStats } from './OrgRail';
import {
  loginVerdict,
  useGithubLogins,
  type LoginVerdict,
} from './useGithubLogins';
import { useTeamAdminAccess } from './useTeamAdminAccess';
import {
  type GrantableRepo,
  grantLabel,
  useGrantableRepos,
} from './useGrantableRepos';
import {
  TeamReviewDialog,
  type Change,
  type StagedChange,
} from './TeamReviewDialog';

type Team = {
  slug: string;
  description: string | null;
  parentTeamId: string | null;
  privacy: string | null;
  memberSource: string | null;
  maintainerSource: string | null;
  members: string[];
  maintainers: string[];
  permission: string | null;
  scopedGrants: { repo: string; permission: string; source: 'literal' | 'computed' }[];
};
type TeamsResponse = { orgs: Record<string, Team[]>; failedOrgs: number };

// Stable org order (matches the backend ORG_FILES set).
const ORG_ORDER = ['m42-platform', 'm42-games', 'm42-sandbox', 'ocpgg'];
/** Ties the rail's tabs to the tree they switch (aria-controls). */
const ORG_PANEL_ID = 'team-admin-org-panel';
// Default repo-access levels (default_team_permissions).
const PERMISSIONS = ['pull', 'triage', 'push', 'maintain', 'admin'];
/**
 * GitHub's UI names for the API values Terraform stores.
 *
 * The dropdown showed the raw values, so "Write" appeared to be missing when it
 * is spelled `push` -- the one value whose GitHub name shares no letters with it.
 * Both are shown: the name the reader knows from GitHub's own settings page, and
 * the value that will actually land in the .tf file they are editing.
 */
const PERMISSION_LABELS: Record<string, string> = {
  pull: 'Read',
  triage: 'Triage',
  push: 'Write',
  maintain: 'Maintain',
  admin: 'Admin',
};
const PRIVACIES = ['closed', 'secret'];

/** "push" -> "Write (push)"; values whose GitHub name adds nothing stay bare. */
const permGloss = (p: string): string =>
  PERMISSION_LABELS[p] && PERMISSION_LABELS[p].toLowerCase() !== p
    ? `${PERMISSION_LABELS[p]} (${p})`
    : PERMISSION_LABELS[p] ?? p;

/**
 * A team as it WILL be once the cart is applied: server values with the staged
 * config edits laid over them.
 *
 * The controls read from this rather than from the server object, so a field
 * shows the value you just chose instead of silently reverting to the current
 * one. Pure and total -- no overlay returns the team untouched.
 */
export function overlayTeam(team: Team, overlay?: ConfigOverlay): Team {
  if (!overlay) return team;
  return {
    ...team,
    ...(overlay.description !== undefined ? { description: overlay.description } : {}),
    ...(overlay.parentTeamId !== undefined
      ? { parentTeamId: overlay.parentTeamId || null }
      : {}),
    ...(overlay.privacy !== undefined ? { privacy: overlay.privacy } : {}),
    ...(overlay.permission !== undefined ? { permission: overlay.permission } : {}),
  };
}

/** Which of the config fields carry a staged value -- drives the "pending" cue. */
export const pendingFieldsOf = (overlay?: ConfigOverlay): ReadonlySet<string> =>
  new Set(overlay ? Object.keys(overlay) : []);

/**
 * Re-derive a team's grant bundles after a picker change.
 *
 * Each pick ACQUIRES the permission selected at pick time, so one run can
 * stage different permissions: pick under push, flip the select, keep picking
 * under pull. A repo holds one permission per team (Terraform's own semantics
 * -- the writer upserts per (team, repo)), an unpick removes the repo from
 * whichever bundle captured it regardless of what the select says now, and
 * survivors keep their captured permission. Returns only the bundles that
 * changed, as (permission, repos) upserts.
 */
export function rebundlePicks(
  bundles: { permission: string; repos: string[] }[],
  currentPerm: string,
  picked: string[],
): { permission: string; repos: string[] }[] {
  const pickedSet = new Set(picked);
  const stagedAnywhere = new Set(bundles.flatMap(b => b.repos));
  const additions = picked.filter(r => !stagedAnywhere.has(r));
  const perms = [...new Set([...bundles.map(b => b.permission), currentPerm])];
  const out: { permission: string; repos: string[] }[] = [];
  for (const perm of perms) {
    const old = bundles.find(b => b.permission === perm)?.repos ?? [];
    const kept = old.filter(r => pickedSet.has(r));
    const next = perm === currentPerm ? [...kept, ...additions] : kept;
    // Content compare, not length: a delta that removes one repo and adds
    // another in a single change nets the same length. Unreachable through
    // MUI's one-option-per-event onChange, but nothing here should rely on
    // that staying true.
    if (next.join('\n') !== old.join('\n')) {
      out.push({ permission: perm, repos: next });
    }
  }
  return out;
}

/**
 * Upsert a scoped-grant bundle into the cart.
 *
 * There is no Stage button: a pick lands in the cart the moment it happens,
 * exactly like a member add. One bundle exists per (org, team, permission) --
 * amending keeps the entry's id and position so the cart row updates in place
 * rather than hopping to the bottom, and an emptied bundle leaves the cart
 * entirely (the last pill's X IS the undo).
 */
export function upsertGrant(
  list: StagedChange[],
  args: {
    org: string;
    slug: string;
    permission: string;
    repos: string[];
    nextId: () => number;
  },
): StagedChange[] {
  const { org, slug, permission, repos } = args;
  const idx = list.findIndex(
    s =>
      s.change.op === 'grantRepoAccess' &&
      s.change.org === org &&
      s.change.slug === slug &&
      s.change.permission === permission,
  );
  if (idx === -1) {
    if (repos.length === 0) return list;
    return [
      ...list,
      {
        id: args.nextId(),
        label: grantLabel(slug, permission, repos),
        change: { op: 'grantRepoAccess', org, slug, permission, repos },
      },
    ];
  }
  if (repos.length === 0) return list.filter((_, i) => i !== idx);
  const next = [...list];
  next[idx] = {
    ...next[idx],
    label: grantLabel(slug, permission, repos),
    change: { op: 'grantRepoAccess', org, slug, permission, repos },
  };
  return next;
}
// Member table columns: GitHub ID (flex) · Role · Access · remove.
/**
 * Client-side mirrors of the BACKEND's guards in teamAdminEdit.ts.
 *
 * The backend is the real gate -- it throws on an unsafe slug/login and treats a
 * duplicate team as a no-op -- so nothing corrupt can reach Terraform either
 * way. What these buy is WHEN you find out: at stage time, next to the field,
 * instead of at the dry-run or in a PR that turns out to change nothing. Keep
 * them identical to the backend; a stricter client silently blocks legal input,
 * a looser one re-creates the late surprise.
 */
const SAFE_SLUG = /^[a-z0-9][a-z0-9-]*$/;
const SAFE_LOGIN = /^[A-Za-z0-9._-]+$/;

// Which message style each verdict severity renders in. A map rather than a
// ternary chain so the four states stay readable as they grow.
const VERDICT_STYLE: Record<
  LoginVerdict['severity'],
  'formError' | 'formWarn' | 'loginOk'
> = {
  error: 'formError',
  warn: 'formWarn',
  ok: 'loginOk',
  none: 'loginOk',
};
/** The backend rejects these in a description (they break the HCL string). */
const UNSAFE_DESC = /["\\\n]/;

/**
 * The single judgement of a would-be team. Pure, so the rules are testable and
 * live in ONE place rather than being re-derived by the button, the field and
 * the tooltip.
 *
 * Returns the first problem in the order a user should fix them, or null. Blank
 * input is not a problem -- an empty form should not scold you before you type.
 */
export function teamProblem(input: {
  slug: string;
  description: string;
  parent: string;
  members: string[];
  /** Slugs that already exist in the org. */
  existingSlugs: readonly string[];
  /** Slugs already staged for creation (excluding the one being amended). */
  stagedSlugs: ReadonlySet<string>;
}): string | null {
  const slug = input.slug.trim();
  const description = input.description.trim();
  const parent = input.parent.trim();
  if (!slug) return null;
  if (!SAFE_SLUG.test(slug)) {
    return 'Use lowercase letters, numbers and hyphens, starting with a letter or number.';
  }
  if (input.existingSlugs.includes(slug)) return `${slug} already exists in this org.`;
  if (input.stagedSlugs.has(slug)) return `${slug} is already staged for creation.`;
  if (description && UNSAFE_DESC.test(description)) {
    return 'Description cannot contain quotes, backslashes or line breaks.';
  }
  if (parent && !input.existingSlugs.includes(parent)) {
    return `No team called ${parent} in this org — pick an existing parent or leave it blank.`;
  }
  const badLogin = input.members
    .map(m => m.trim())
    .find(m => m && !SAFE_LOGIN.test(m));
  if (badLogin) return `"${badLogin}" is not a valid GitHub login.`;
  return null;
}

/** Per-tab cart key. Bumped if StagedChange's shape ever changes. */
const CART_KEY = 'm42.teamAdmin.cart.v1';
/** Every op the cart may carry -- the allow-list a restored cart is checked against. */
const CART_OPS = [
  'addMember',
  'removeMember',
  'setPermission',
  'editConfig',
  'addTeam',
  'removeTeam',
];

const isStr = (v: unknown): v is string => typeof v === 'string';
const isOptStr = (v: unknown): boolean => v === undefined || isStr(v);
const isStrArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every(isStr);

/**
 * Does this change carry the payload its op actually needs?
 *
 * Checking `op` alone is not enough: an `addTeam` with no `members` used to
 * survive restore, and then GhostTeamRow's `ghost.members.length` threw at
 * mount -- a corrupt cart entry taking down the whole page rather than being
 * dropped. Each arm lists the fields that op's consumers read.
 */
function changePayloadValid(c: Record<string, unknown>): boolean {
  switch (c.op) {
    case 'addMember':
    case 'removeMember':
      return isStr(c.team) && isStr(c.login);
    case 'setPermission':
      return isStr(c.slug) && isStr(c.permission);
    case 'editConfig':
      // Every field is optional on its own, but an editConfig carrying none of
      // them is a no-op that would hold a cart row and produce nothing.
      return (
        isStr(c.slug) &&
        isOptStr(c.description) &&
        isOptStr(c.parentTeamId) &&
        isOptStr(c.privacy) &&
        (c.description !== undefined ||
          c.parentTeamId !== undefined ||
          c.privacy !== undefined)
      );
    case 'addTeam':
      return (
        isStr(c.slug) &&
        isStr(c.description) &&
        isOptStr(c.parentTeamId) &&
        isStrArray(c.members)
      );
    case 'removeTeam':
      return isStr(c.slug);
    case 'revokeRepoAccess':
      return isStr(c.slug) && isStr(c.repo);
    case 'grantRepoAccess':
      // repos must be non-empty: the backend rejects an empty grant, so an empty
      // one surviving restore would hold a cart row that can only fail at dry-run.
      return (
        isStr(c.slug) &&
        isStr(c.permission) &&
        isStrArray(c.repos) &&
        (c.repos as string[]).length > 0
      );
    default:
      return false;
  }
}

/**
 * Reads the persisted cart, VALIDATING every entry rather than casting it.
 *
 * The cart round-trips through sessionStorage, so it re-enters as `unknown`
 * from a store the user (or a stale build) can edit. An unchecked cast would
 * let a malformed entry reach the dry-run and the PR body -- on the one surface
 * whose entire promise is "nothing ships that you have not reviewed". Anything
 * unrecognisable is dropped silently: a partially-restored cart is still
 * visible in full on screen before it can become a PR.
 */
export function restoreCart(): StagedChange[] {
  try {
    const raw = sessionStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: StagedChange[] = [];
    for (const entry of parsed) {
      if (!entry || typeof entry !== 'object') continue;
      const s = entry as Record<string, unknown>;
      const c = s.change as Record<string, unknown> | undefined;
      if (
        typeof s.id !== 'number' ||
        !Number.isFinite(s.id) ||
        typeof s.label !== 'string' ||
        !c ||
        typeof c !== 'object' ||
        typeof c.op !== 'string' ||
        !CART_OPS.includes(c.op) ||
        typeof c.org !== 'string' ||
        !changePayloadValid(c)
      ) {
        continue;
      }
      out.push(entry as StagedChange);
    }
    return out;
  } catch {
    return [];
  }
}

// The inline-editable team_configs fields (each optional; staged as editConfig).
type ConfigPatch = {
  description?: string;
  parentTeamId?: string;
  privacy?: string;
};

// Optimistic view of the cart for the active org, so the tree reflects staged
// (uncommitted) edits before the PR exists. Keyed lookups carry the staged
// change id so a row can undo just its own edit.
type GhostTeam = {
  id: number;
  slug: string;
  description: string;
  parentTeamId: string;
  members: string[];
};
/**
 * The staged VALUES for a team's config fields, so an edited control can show
 * what it will become.
 *
 * editedTeams below records only THAT a team was touched -- enough for the badge,
 * and the reason the Repo access field reverted to its server value the moment a
 * change was staged: the new value existed in the cart and nowhere the field
 * could read it.
 */
type ConfigOverlay = {
  description?: string;
  parentTeamId?: string;
  privacy?: string;
  permission?: string;
};
type PendingState = {
  removedMembers: Map<string, number>; // `${team}:${login}` -> id
  addedMembers: Map<string, { login: string; id: number }[]>; // team -> adds
  removedTeams: Map<string, number>; // slug -> id
  editedTeams: Set<string>; // slug
  configEdits: Map<string, ConfigOverlay>; // slug -> staged field values
  /**
   * Staged NEW teams, keyed by parent slug ('' = a root team), so a ghost row
   * renders at the level the team will actually occupy. Without this, addTeam
   * was the one staged op with no in-tree preview: it existed only in the
   * cart, so the tree said "nothing changed" while a team creation was queued.
   */
  addedTeams: Map<string, GhostTeam[]>;
  /** Staged scoped-grant bundles, slug -> one entry per permission. */
  grantEdits: Map<string, { id: number; permission: string; repos: string[] }[]>;
  /** Staged revocations of EXISTING grants, slug -> repo -> staged id. */
  grantRevokes: Map<string, Map<string, number>>;
  unstage: (id: number) => void;
};
const emptyPending: PendingState = {
  removedMembers: new Map(),
  addedMembers: new Map(),
  removedTeams: new Map(),
  editedTeams: new Set(),
  configEdits: new Map(),
  addedTeams: new Map(),
  grantEdits: new Map(),
  grantRevokes: new Map(),
  unstage: () => {},
};
const PendingContext = createContext<PendingState>(emptyPending);
const usePending = () => useContext(PendingContext);

/**
 * The explainer's stages, as a function of what is queued.
 *
 * The three stages ARE the pipeline this page implements, and the page knows
 * whether the last one is reachable, so it can say so instead of describing it.
 * The rule is deliberate: with NOTHING queued no step carries a status at all
 * and the explainer renders as plain documentation. Marking progress with no
 * data behind it would be inventing it -- "Find" is not finished just because
 * the page loaded, and the browse/edit loop is not linear. Only the last stage
 * is genuinely gated (by the cart), so only it earns a live state.
 */
export function howItWorksSteps(stagedCount: number): HowItWorksStep[] {
  const queued = stagedCount > 0;
  return [
    {
      title: 'Find',
      status: queued ? 'done' : undefined,
      body: (
        <>
          Search once — it covers every org, and each org reports what it
          matched, so a person in another org is never hidden. Then expand a team
          to see its members, each one's role, and the repo access it grants.
        </>
      ),
    },
    {
      title: 'Edit inline',
      status: queued ? 'done' : undefined,
      body: (
        <>
          Add or remove members, change repo access or privacy, edit details, or
          create a team. Every edit is queued — nothing is applied yet, and you
          can undo any of it.
        </>
      ),
    },
    {
      title: 'Review & save',
      status: queued ? 'active' : undefined,
      note: queued ? `${stagedCount} queued` : undefined,
      body: (
        <>
          Open the cart to see a live diff, then submit one PR to{' '}
          <code>.github-private</code>. Changes apply only when{' '}
          <code>atlantis apply</code> is run.
        </>
      ),
    },
  ];
}

/**
 * The one line the header keeps while the explainer is collapsed. With changes
 * queued it reports them, because that is the fact most worth not losing behind
 * a collapsed panel; otherwise it restates the promise the whole page rests on.
 */
export function howItWorksSummary(stagedCount: number): string {
  if (stagedCount > 0) {
    return `${stagedCount} change${
      stagedCount === 1 ? '' : 's'
    } queued — review and ship them as one PR.`;
  }
  return 'Browse, edit inline, then ship one reviewed PR. Nothing applies until atlantis apply.';
}

const useStyles = makeStyles(theme => ({
  usageWarn: {
    color: theme.palette.warning.main,
    fontSize: '0.8125rem',
    marginTop: theme.spacing(2),
  },
  search: {
    width: '100%',
    maxWidth: 420,
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  // Pushed to the trailing edge, and kept as one unit so the timestamp never
  // wraps away from the button it describes.
  searchEnd: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  loadedAt: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  // The docked cart: fixed, so it survives any scroll depth in a long tree.
  // zIndex sits above the content but below MUI's modal layer (1300), so the
  // review dialog it opens is never underlapped by its own trigger.
  cartDock: {
    position: 'fixed',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: 1200,
    boxShadow: theme.shadows[6],
    textTransform: 'none',
    /**
     * Clear Backstage's MOBILE BOTTOM NAV.
     * Below sm the shell renders a fixed BottomNavigation (~56px). Docked at
     * bottom:24 the cart landed underneath it and the nav swallowed every
     * click -- the button was visible and completely unusable. Caught by the
     * verification round, not by reading the code.
     */
    [theme.breakpoints.down('sm')]: {
      bottom: 72,
      right: theme.spacing(2),
    },
  },
  // Screen-reader-only: the live region. display:none would silence it.
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  tree: { width: '100%' },
  // Indented container for a team's expanded content. Nesting compounds the
  // indent; the 1px guide (under the chevron column) makes the hierarchy read.
  group: {
    marginLeft: 12, // guide sits in the chevron gutter
    paddingLeft: 20, // content lands under the team slug
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  /**
   * A sub-team's expanded body is recessed.
   *
   * A parent and its child rendered identical panels -- same ACCESS/EDIT header,
   * description, attribute row and DELETE TEAM -- so two stacked blocks gave no
   * clue where the parent ended and the child began. Indentation alone was
   * carrying the whole hierarchy. The tint compounds with depth, so each level
   * sits visibly inside the one above it.
   */
  groupNested: {
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
  },
  // --- team branch row ---
  teamRow: {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 44, // uniform across rows whether or not a permission chip is present
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1, 0.5, 0),
    background: 'none',
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    color: 'inherit',
    transition: 'background-color 120ms ease',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.04)'
          : 'rgba(0,0,0,0.025)',
    },
    '&:hover .teamSlug': { color: theme.palette.primary.main },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
  twisty: {
    color: theme.palette.text.secondary,
    transition: 'transform 160ms cubic-bezier(0.25, 1, 0.5, 1)',
    flexShrink: 0,
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  twistyOpen: { transform: 'rotate(90deg)' },
  slug: { fontWeight: 600, fontSize: '0.9375rem', flexShrink: 0 },
  // A nested team's name steps down one weight and size, so the row states its
  // own level without the reader measuring the indent.
  slugNested: { fontWeight: 500, fontSize: '0.875rem' },
  parentChip: { height: 18, fontSize: '0.65rem' },
  desc: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  permChip: {
    height: 20,
    fontSize: '0.7rem',
    flexShrink: 0,
  },
  count: {
    marginLeft: 'auto',
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    paddingLeft: theme.spacing(1),
  },
  // --- inline-editable details block (description row + config row) ---
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    padding: theme.spacing(1, 0, 1.5, 0),
  },
  detailLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    marginBottom: 2,
  },
  // Top-aligned, not centered: two of these fields always carry helper text
  // (the blast-radius line, the greyed-repos line), and centering the taller
  // input+helper block shoved its input out of line with its neighbors.
  configRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  // "staged" under a field whose value came from the cart. Deliberately the
  // primary ink, not error red: a pending change is state, not a problem.
  pendingHint: {
    color: 'inherit',
    opacity: 0.75,
    fontVariant: 'small-caps',
    letterSpacing: '0.04em',
  },
  // The scoped-repo picker: wide enough for repo names plus chips; the list
  // itself grows downward, so width is the only dimension that matters here.
  // min() so the 320px floor yields on narrow screens instead of clipping at
  // the viewport edge (seen at 390px, where the indent left less than 320).
  grantPicker: { minWidth: 'min(320px, 100%)', flex: '1 1 320px', maxWidth: 560 },
  // MUI dims disabled options to opacity .38, which rendered this picker's
  // refusal reason at 1.84:1 -- the one thing the design exists to say was the
  // least legible text on the page (measured, WCAG 1.4.3). Disabled-ness is
  // conveyed in INK instead: secondary text and an italic reason, both >=7:1 on
  // the popup paper in each theme, with aria-disabled and non-selectability
  // carrying the state for everyone else.
  grantOptionDisabled: {
    '&[aria-disabled="true"]': { opacity: 1 },
    '&[aria-disabled="true"] p': { color: theme.palette.text.secondary },
  },
  grantReason: { fontStyle: 'italic' },
  detailField: { minWidth: 160 },
  // Wide enough that the shrunk "Default access (all repos)" label fits its
  // notch on one line; at detailField width it wrapped and collided with the
  // selected value.
  defaultAccessField: { minWidth: 224 },
  // Grants the team already holds, shown as state above the staging controls.
  grantChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1),
  },
  // A staged REVOKE of an existing grant: the red removal language members
  // use, with the chip's icon flipped to undo.
  grantChipRevoked: {
    backgroundColor: 'rgba(248, 81, 73, 0.12)',
    color: theme.palette.error.dark,
    '& [class*="MuiChip-label"]': { textDecoration: 'line-through' },
    '& svg': { color: theme.palette.error.dark, opacity: 0.7 },
    '& svg:hover': { opacity: 1 },
  },
  // An existing grant a staged bundle supersedes (same repo re-picked): still
  // legible, visibly outgoing.
  grantChipReplaced: {
    '& [class*="MuiChip-label"]': {
      textDecoration: 'line-through',
      color: theme.palette.text.secondary,
    },
  },
  // The draft set: repos picked but not yet staged. Green ghost language, the
  // same "queued, not real yet" tint as a staged member add, and each pill is
  // removable -- the search box stays a search box instead of piling tags.
  grantDraft: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
  },
  grantDraftPill: {
    backgroundColor: 'rgba(46, 160, 67, 0.14)',
    color: theme.palette.success.dark,
    animation: '$ghostIn 200ms ease-out',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    '& .MuiChip-deleteIcon, & [class*="deleteIcon"]': {
      color: theme.palette.success.dark,
      opacity: 0.7,
      '&:hover': { opacity: 1 },
    },
  },
  descEdit: { maxWidth: 520, width: '100%' },
  // Display-mode description: reads as content (ink), reveals a pencil on hover.
  descShow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    cursor: 'text',
    borderRadius: 4,
    padding: theme.spacing(0.25, 0.75),
    margin: theme.spacing(-0.25, -0.75),
    maxWidth: '70ch',
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:hover .editPencil': { opacity: 1 },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  descEmpty: { color: theme.palette.text.hint, fontStyle: 'italic' },
  editPencil: {
    opacity: 0,
    fontSize: 14,
    color: theme.palette.text.secondary,
    transition: 'opacity 120ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  deleteBtn: {
    color: theme.palette.error.main,
    fontSize: '0.8125rem',
  },
  // --- members ---
  membersToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 0.75, 0.75, 0.25),
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    font: 'inherit',
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontWeight: 500,
    borderRadius: 4,
    '&:hover': { color: theme.palette.text.primary },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  // Members render as a grid table; header + rows share the same columns so
  // GitHub ID / Role / Access / action line up. (Grid, because it's 2D data.)
  memberTable: {
    paddingLeft: 22, // align under the toggle label, past its chevron
    paddingTop: theme.spacing(0.5),
  },
  /**
   * The roster is a LIST of people, not a table.
   *
   * It was a 3-column grid, and two of those columns were constants: Access is
   * `team.permission` passed to every row, so a 30-person team printed `push`
   * 30 times; Role printed `MEMBER` 24 of 30. Stretched across the panel to
   * hold them, the login cell measured 627px for ~150px of text, leaving ~470px
   * of void between a person and their role. Both constants are now stated once
   * -- access in the meta line, role as the group heading -- which frees the
   * roster to be a dense grid of names.
   */
  // --- read-first team attributes (Access zone) ---
  /**
   * At rest a team's attributes are label/value pairs, not form controls.
   *
   * The strip this replaces was three controls, and two of them rendered EMPTY:
   * a null parent and a null permission both produced a blank box under a
   * floating label, which reads as "failed to load" rather than "grants
   * nothing". Editing is now a deliberate act, so nothing on screen at rest can
   * look broken.
   */
  zoneHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 28,
    flexWrap: 'wrap',
  },
  zoneLabel: {
    margin: 0,
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  /**
   * The team's actions, clustered at the trailing edge of the zone header.
   *
   * Delete used to occupy a right-aligned row of its own below the attributes,
   * which read as an orphan and added a row of vertical noise. It sits with Edit
   * now -- both are team-level actions -- but with a deliberately wide gap and
   * Delete LAST, so the destructive one is at the extreme edge rather than
   * between two things a mis-click could reach. Staging a delete is undoable
   * (the button becomes Undo delete and the row strikes through), so adjacency
   * here costs a recoverable mistake, not a lost team.
   */
  zoneActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  attrList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5, 3),
    margin: theme.spacing(0.5, 0, 0, 0),
    padding: 0,
  },
  // Tight WITHIN a pair, generous BETWEEN pairs (the attrList gap) -- so a key
  // reads as belonging to the value beside it. A minWidth on the key pushed
  // "closed" ~84px away from "Privacy", which grouped the wrong things.
  attrRow: { display: 'flex', alignItems: 'baseline', gap: theme.spacing(0.75) },
  attrKey: {
    margin: 0,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  attrValue: {
    margin: 0,
    fontSize: '0.8125rem',
    fontFamily: 'monospace',
    color: theme.palette.text.primary,
  },
  // Absence is stated in words, never left as a blank.
  attrValueEmpty: {
    margin: 0,
    fontSize: '0.8125rem',
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
  },
  rosterGroup: { paddingBottom: theme.spacing(1) },
  // The repeated role chip, promoted to a heading: said once per group instead
  // of once per person.
  rosterGroupLabel: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    padding: theme.spacing(0, 0, 0.5, 0),
  },
  rosterGroupCount: {
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 500,
    letterSpacing: 0,
  },
  /**
   * Wrapping flex, NOT grid tracks.
   *
   * `repeat(auto-fill, ...)` reserves every track whether or not anything fills
   * it, so a 2-person team rendered as three 279px columns: two names in
   * columns 1 and 2 and a phantom third. It read as a form field with a stray
   * word beside it. Flex items claim only the width they are given, so two
   * people render as two cells.
   */
  rosterFlex: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.25, 1.5),
    margin: 0,
    padding: 0,
    listStyle: 'none',
    // A uniform basis keeps columns aligned row to row without reserving
    // tracks; the floor is the longest login the fleet has plus its action.
    '& > li': { flex: '0 0 212px', maxWidth: '100%' },
    [theme.breakpoints.down('xs')]: {
      '& > li': { flex: '1 1 100%' },
    },
  },
  memberLoginLink: {
    fontFamily: 'monospace',
    fontSize: '0.8125rem',
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': { textDecoration: 'underline' },
  },
  memberRowHit: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  accessNone: { color: theme.palette.text.hint },
  lockNote: {
    justifySelf: 'end',
    fontSize: '0.625rem',
    color: theme.palette.text.hint,
    fontStyle: 'italic',
  },
  memberLegend: {
    color: theme.palette.text.secondary,
    fontSize: '0.6875rem',
    lineHeight: 1.5,
    paddingTop: theme.spacing(0.75),
    maxWidth: '72ch',
  },
  removeMember: {
    padding: 4,
    opacity: 0,
    transition: 'opacity 120ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    '&:focus-visible': { opacity: 1 },
  },
  /**
   * One person. Name and action sit in the same narrow cell, so the ~470px void
   * the wide table opened between them cannot exist.
   */
  memberCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
    minHeight: 28,
    padding: theme.spacing(0.25, 0.75),
    margin: theme.spacing(0, -0.75),
    borderRadius: 4,
    fontSize: '0.8125rem',
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:hover $removeMember': { opacity: 1 },
    // Keyboard parity: the action must appear on focus, not hover alone.
    '&:focus-within $removeMember': { opacity: 1 },
  },
  /**
   * The authored moment: a roster arrives as a sweep rather than a slab. The
   * per-cell delay comes from a custom property the caller caps, so even a
   * 30-person team finishes inside ~240ms.
   */
  memberCellIn: {
    animation: '$cellIn 220ms cubic-bezier(0.16, 1, 0.3, 1) both',
    animationDelay: 'var(--cell-delay, 0ms)',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  '@keyframes cellIn': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'none' },
  },
  cellPush: { marginLeft: 'auto' },
  /**
   * Marks the MINORITY. A role chip on every row printed MEMBER 24 times on a
   * 30-person team, which is why it was removed; marking only maintainers gives
   * each name a self-describing badge without reintroducing that noise. Icon +
   * accessible title + the group heading, so the distinction never rests on
   * colour alone.
   */
  maintainerMark: {
    fontSize: 13,
    flexShrink: 0,
    color: theme.palette.primary.main,
  },
  // Maintainer names carry a touch more weight than plain members, so the two
  // groups differ typographically as well as by badge.
  maintainerLogin: { fontWeight: 600 },
  /**
   * Sub-teams were plain siblings of the parent's own config strip, so a child
   * team read as one of the parent's attributes. A labelled band says what they
   * are before the parent's own roster appears.
   */
  subTeams: { marginTop: theme.spacing(0.5) },
  subTeamsLabel: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    // A rule plus generous space ABOVE and tight below: this is where a team's
    // own attributes end and its children begin, which is exactly the boundary
    // that was invisible.
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 0, 0.25, 0),
  },
  // Sits inside the roster container, so it shares the roster's left edge
  // instead of being a sibling offset 22px from it.
  // Adjacent to its "Add someone" label, NOT pushed to the trailing edge --
  // separating a label from its own control by 500px is the same mistake the
  // member rows used to make.
  addInline: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  addField: { minWidth: 220 },
  // --- inline new-team ---
  newTeamForm: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    margin: theme.spacing(0.5, 0),
  },
  // The form's single message line. flexBasis 100% makes it own a row of its
  // own in the wrapping flex container, so it never affects field heights.
  formError: {
    flexBasis: '100%',
    order: 99,
    fontSize: '0.75rem',
    color: theme.palette.error.main,
    paddingLeft: theme.spacing(0.5),
  },
  formWarn: {
    flexBasis: '100%',
    order: 99,
    fontSize: '0.75rem',
    color: theme.palette.warning.main,
    paddingLeft: theme.spacing(0.5),
  },
  // Confirmations and the in-flight "checking…" line. Deliberately quieter than
  // the error/warn colours: a login that checks out should reassure, not shout.
  loginOk: {
    flexBasis: '100%',
    order: 99,
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(0.5),
  },
  addLink: {
    color: theme.palette.primary.main,
    fontSize: '0.8125rem',
    justifyContent: 'flex-start',
    textTransform: 'none',
  },
  sourceNote: {
    color: theme.palette.text.hint,
    fontSize: '0.6875rem',
    fontFamily: 'monospace',
    paddingTop: theme.spacing(0.5),
  },
  empty: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    padding: theme.spacing(1, 0),
  },
  // --- optimistic / pending-change affordances ---
  '@keyframes ghostIn': {
    from: { opacity: 0, transform: 'translateY(-2px)' },
    to: { opacity: 1, transform: 'none' },
  },
  memberRemoving: {
    backgroundColor: 'rgba(248, 81, 73, 0.12)',
    '& $memberLoginLink': {
      textDecoration: 'line-through',
      opacity: 0.6,
    },
  },
  memberAdding: { backgroundColor: 'rgba(46, 160, 67, 0.14)' },
  pendingTag: {
    height: 16,
    fontSize: '0.6rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    justifySelf: 'end',
  },
  pendingActionBtn: { justifySelf: 'end', padding: 4 },
  // team header pending markers
  /**
   * One geometry for every staged-state marker.
   *
   * These were two chip styles at different heights, sizes and letterspacing,
   * coloured through MUI's `color` prop -- and `willDeleteChip` was rendering
   * the green "new" chip too, so the name lied. Same pill, semantic tint per
   * KIND of change, because "will be removed" and "will be added" are genuinely
   * different facts and colour is doing real work there.
   */
  stateChip: {
    height: 17,
    border: 0,
  },
  // MUI v4 styles .MuiChip-label directly, so type set on the root is ignored:
  // a 0.625rem root still measured 12px on the label. Passed through the Chip's
  // `classes` API instead of fought with specificity.
  stateChipLabel: {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
    fontSize: '0.625rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  stateChipNew: {
    color: theme.palette.success.dark,
    backgroundColor: `${theme.palette.success.main}26`,
  },
  stateChipDelete: {
    color: theme.palette.error.dark,
    backgroundColor: `${theme.palette.error.main}26`,
  },
  editedDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.main,
    flexShrink: 0,
    animation: '$ghostIn 200ms ease-out',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  slugStrike: { textDecoration: 'line-through', opacity: 0.6 },
  // A staged NEW team: the same green ghost language as a staged member add,
  // so "queued, not real yet" reads identically everywhere in the tree.
  ghostTeamRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 44,
    padding: theme.spacing(0.5, 1, 0.5, 0),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'rgba(46, 160, 67, 0.14)',
    animation: '$ghostIn 200ms ease-out',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  // Aligns the ghost's slug with real teams' slugs (which sit after a chevron).
  ghostTeamIndent: { width: 20, flexShrink: 0 },
  ghostTeamMeta: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginLeft: 'auto',
    paddingRight: theme.spacing(1),
    whiteSpace: 'nowrap',
  },
  countDelta: {
    marginLeft: theme.spacing(0.75),
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  countAdd: { color: theme.palette.success.main },
  countRemove: { color: theme.palette.error.main },
  // --- two-column layout: tree (main) + access aside (fills the right space) ---
  // No maxWidth: the grid spans the full content width so the aside sits flush
  // against the right edge instead of leaving dead space on wide monitors. The
  // main column flexes (minmax(0, 1fr)); the aside stays a fixed 308px.
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 308px',
    gap: theme.spacing(3),
    alignItems: 'start',
    [theme.breakpoints.down('md')]: { gridTemplateColumns: '1fr' },
  },
  mainCol: { minWidth: 0 },
  sideCol: {
    position: 'sticky',
    top: theme.spacing(2),
    [theme.breakpoints.down('md')]: { position: 'static' },
  },
  accessIntro: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  accessLabel: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.75),
  },
  accessChips: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(0.5) },
  accessGroupChip: { height: 22, fontWeight: 500 },
  accessUser: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 4,
    backgroundColor: theme.palette.action.hover,
  },
  accessHow: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginTop: theme.spacing(0.5),
  },
}));

/**
 * The team row's accessible name, comma-separated.
 *
 * The row is built from chips and spans with no separators, so its own text
 * content ran together as one word-salad string. Staged state leads, because
 * "will delete" changes what every other number on the row means.
 */
export function teamRowLabel(
  team: {
    slug: string;
    description: string | null;
    permission: string | null;
    members: string[];
  },
  state: { willDelete: boolean; edited: boolean },
): string {
  const parts = [team.slug];
  if (state.willDelete) parts.push('queued for deletion');
  else if (state.edited) parts.push('has uncommitted edits');
  if (team.description) parts.push(team.description);
  if (team.permission) parts.push(`${team.permission} access`);
  parts.push(
    `${team.members.length} ${team.members.length === 1 ? 'member' : 'members'}`,
  );
  return parts.join(', ');
}

/** One person in a roster group. */
export type RosterPerson = {
  key: string;
  login: string;
  /**
   * Org owner, therefore a maintainer of this team. Carried per person (not just
   * implied by the group heading) so a name is self-describing wherever it is
   * read -- the heading is off-screen as soon as a roster is a few rows tall.
   */
  isMaintainer: boolean;
  removable: boolean;
  pending: 'add' | 'remove' | null;
  /** The staged change to drop on Undo; undefined when nothing is staged. */
  stagedId?: number;
};

export type RosterGroup = { label: string; people: RosterPerson[] };

/**
 * Splits a team's roster into role groups.
 *
 * The role used to be a chip on every row, which printed MEMBER 24 times on a
 * 30-person team. As a group heading it is said once, and the rows lose a column
 * they never varied. Empty groups are dropped rather than rendered as a heading
 * with nothing under it.
 *
 * `sharedSource` matters for removability: when a team's maintainers come from a
 * different local than its members (org_owners, say), they cannot be dropped by
 * editing this team, so they get the org-wide note instead of a remove control.
 */
export function rosterGroups(input: {
  members: readonly string[];
  maintainers: ReadonlySet<string>;
  /**
   * Every org owner known in this org (the union of every team's maintainers).
   * Used only to predict the group a STAGED add will land in; committed people
   * are classified by the team's own maintainer set. A person in org_owners who
   * belongs to no team yet is not in here, so such an add is predicted as a
   * member -- wrong but harmless, and it corrects itself once applied.
   */
  orgOwners: ReadonlySet<string>;
  ghostAdds: readonly { login: string; id: number }[];
  removedMembers: ReadonlyMap<string, number>;
  teamSlug: string;
  canEdit: boolean;
  sharedSource: boolean;
}): RosterGroup[] {
  const {
    members,
    maintainers,
    orgOwners,
    ghostAdds,
    removedMembers,
    teamSlug,
    canEdit,
    sharedSource,
  } = input;

  const maintainerPeople: RosterPerson[] = [];
  const memberPeople: RosterPerson[] = [];
  for (const login of members) {
    const isMaintainer = maintainers.has(login);
    const stagedId = removedMembers.get(`${teamSlug}:${login}`);
    const person: RosterPerson = {
      key: login,
      login,
      isMaintainer,
      removable: canEdit && (!isMaintainer || sharedSource),
      pending: stagedId !== undefined ? 'remove' : null,
      stagedId,
    };
    (isMaintainer ? maintainerPeople : memberPeople).push(person);
  }
  // Staged joins land with the members they will become, at the end, so the
  // group they belong to is obvious without a per-row role marker.
  for (const g of ghostAdds) {
    // An org owner joins AS a maintainer, so a staged add lands in whichever
    // group it will actually belong to once applied -- not always Members.
    // Matched against the ORG-wide owner set: the team's own maintainer set
    // cannot contain someone who is not in the team yet.
    const owner = orgOwners.has(g.login);
    (owner ? maintainerPeople : memberPeople).push({
      key: `add-${g.login}`,
      login: g.login,
      isMaintainer: owner,
      removable: false,
      pending: 'add',
      stagedId: g.id,
    });
  }

  return [
    { label: 'Maintainers', people: maintainerPeople },
    { label: 'Members', people: memberPeople },
  ].filter(g => g.people.length > 0);
}

// One member of a roster group: GitHub ID (profile link) and an inline action.
// `pending` reflects an uncommitted edit -- struck through when queued for
// removal, or tagged `new` when queued to be added -- each with an Undo that
// drops just that staged change.
function MemberRow({
  login,
  isMaintainer,
  hit,
  removable,
  pending,
  index,
  onRemove,
  onUndo,
}: {
  login: string;
  isMaintainer: boolean;
  hit: boolean;
  removable: boolean;
  pending: 'add' | 'remove' | null;
  /** Position within its group, for the staggered entrance. */
  index: number;
  onRemove: () => void;
  onUndo: () => void;
}) {
  const classes = useStyles();
  let rowCls = '';
  if (pending === 'remove') rowCls = ` ${classes.memberRemoving}`;
  else if (pending === 'add') rowCls = ` ${classes.memberAdding}`;
  // Capped: past ~20 cells the sweep would outlast the reader's patience, so
  // the tail lands together rather than trickling for a second and a half.
  const delay = Math.min(index, 20) * 12;
  return (
    <li
      className={`${classes.memberCell} ${classes.memberCellIn}${rowCls}`}
      style={{ '--cell-delay': `${delay}ms` } as React.CSSProperties}
    >
      {isMaintainer && (
        <Tooltip title="Org owner — a maintainer in every team they belong to">
          <VpnKeyIcon
            className={classes.maintainerMark}
            titleAccess="Org owner, maintainer"
          />
        </Tooltip>
      )}
      <Link
        to={`https://github.com/${login}`}
        className={`${classes.memberLoginLink}${
          isMaintainer ? ` ${classes.maintainerLogin}` : ''
        }${hit ? ` ${classes.memberRowHit}` : ''}`}
        title={`Open ${login} on GitHub`}
      >
        {login}
      </Link>
      {pending === 'add' && (
        <Chip
          className={`${classes.stateChip} ${classes.stateChipNew}`}
          classes={{ label: classes.stateChipLabel }}
          size="small"
          label="new"
        />
      )}
      <span className={classes.cellPush} />
      {renderAction()}
    </li>
  );

  function renderAction() {
    if (pending) {
      return (
        <Tooltip title={pending === 'remove' ? 'Undo removal' : 'Undo add'}>
          <IconButton
            className={classes.pendingActionBtn}
            size="small"
            aria-label={`Undo ${pending} ${login}`}
            onClick={onUndo}
          >
            <UndoIcon style={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      );
    }
    if (removable) {
      return (
        <IconButton
          className={classes.removeMember}
          size="small"
          aria-label={`Remove ${login}`}
          onClick={onRemove}
        >
          <CloseIcon style={{ fontSize: 15 }} />
        </IconButton>
      );
    }
    return (
      <Tooltip title="Org owners are managed org-wide, not per team">
        <span className={classes.lockNote}>org-wide</span>
      </Tooltip>
    );
  }
}

// "N members" disclosure: collapsed by default, expands to leaf rows + add row.
function MembersDisclosure({
  team,
  orgLogins,
  orgOwners,
  org,
  query,
  forceOpen,
  onMember,
}: {
  team: Team;
  orgLogins: string[];
  orgOwners: ReadonlySet<string>;
  org: string;
  query: string;
  forceOpen: boolean;
  onMember: (team: Team, login: string, action: 'add' | 'remove') => void;
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [newLogin, setNewLogin] = useState('');
  const expanded = forceOpen || open;
  const canEdit = Boolean(team.memberSource);
  // Maintainers and members come from the same local on most teams; when they
  // don't (e.g. m42-admins draws maintainers from org_owners), maintainers
  // aren't part of this team's editable list and can't be removed here.
  const sharedSource = team.maintainerSource === team.memberSource;
  const maintainers = useMemo(() => new Set(team.maintainers), [team.maintainers]);

  const pending = usePending();
  // Staged adds for this team that aren't already committed -> ghost rows.
  const committedSet = useMemo(() => new Set(team.members), [team.members]);
  const ghostAdds = (pending.addedMembers.get(team.slug) ?? []).filter(
    a => !committedSet.has(a.login),
  );
  const removeCount = team.members.filter(m =>
    pending.removedMembers.has(`${team.slug}:${m}`),
  ).length;

  // Verify the typed login against GitHub before it can be staged.
  const typed = newLogin.trim();
  const lowered = typed.toLowerCase();
  // Already here, or already queued. Both are no-ops the backend silently
  // discards, so they're caught at the field rather than surfacing as
  // "1 no-op(s) skipped" after review -- and matched case-INSENSITIVELY,
  // because GitHub logins are, so `JDoe` and `jdoe` are one person.
  const alreadyMember =
    typed !== '' && team.members.some(m => m.toLowerCase() === lowered);
  const alreadyStaged =
    typed !== '' && ghostAdds.some(a => a.login.toLowerCase() === lowered);
  // A committed or queued member needs no round-trip: they demonstrably exist.
  const needsCheck =
    typed && !alreadyMember && !alreadyStaged ? [typed] : [];
  const { checks, checking } = useGithubLogins(needsCheck, org);

  let verdict: LoginVerdict;
  if (alreadyMember) {
    verdict = {
      severity: 'warn',
      text: `${typed} is already in ${team.slug}.`,
      blocking: true,
    };
  } else if (alreadyStaged) {
    verdict = {
      severity: 'warn',
      text: `${typed} is already staged to join ${team.slug}.`,
      blocking: true,
    };
  } else {
    verdict = loginVerdict(checks.get(lowered), org);
  }

  let addHint = '';
  if (verdict.blocking) addHint = verdict.text;
  else if (checking) addHint = 'Checking this login with GitHub…';

  const roleGroups = useMemo(
    () =>
      rosterGroups({
        members: team.members,
        maintainers,
        orgOwners,
        ghostAdds,
        removedMembers: pending.removedMembers,
        teamSlug: team.slug,
        canEdit,
        sharedSource,
      }),
    [
      team.members,
      team.slug,
      maintainers,
      orgOwners,
      ghostAdds,
      pending.removedMembers,
      canEdit,
      sharedSource,
    ],
  );

  const submitAdd = () => {
    if (!typed || verdict.blocking || checking) return;
    // Stage GitHub's own spelling when it differs, so the same person can't
    // enter the Terraform list twice under two casings.
    const check = checks.get(lowered);
    onMember(team, check?.canonicalLogin ?? typed, 'add');
    setNewLogin('');
  };

  return (
    <div>
      <button
        type="button"
        className={classes.membersToggle}
        aria-expanded={expanded}
        onClick={() => setOpen(o => !o)}
      >
        <ChevronRightIcon
          fontSize="small"
          className={`${classes.twisty}${expanded ? ` ${classes.twistyOpen}` : ''}`}
        />
        {team.members.length}{' '}
        {team.members.length === 1 ? 'member' : 'members'}
        {ghostAdds.length > 0 && (
          <span className={`${classes.countDelta} ${classes.countAdd}`}>
            +{ghostAdds.length}
          </span>
        )}
        {removeCount > 0 && (
          <span className={`${classes.countDelta} ${classes.countRemove}`}>
            −{removeCount}
          </span>
        )}
      </button>
      <Collapse in={expanded} unmountOnExit>
        <div className={classes.memberTable}>
          {/* The add control leads the roster instead of trailing it.
              It used to sit after every name AND after the legend paragraph, so
              on a 30-person team the only way to add someone was below a screen
              of rows -- the reason "how do I add a member" had no obvious
              answer. */}
          {canEdit && (
            <div className={classes.zoneHeader}>
              <h4 className={classes.zoneLabel}>Add someone</h4>
              <Box className={classes.addInline}>
                <Autocomplete
                  freeSolo
                  className={classes.addField}
                  options={orgLogins.filter(l => !team.members.includes(l))}
                  inputValue={newLogin}
                  onInputChange={(_e, v) => setNewLogin(v)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      placeholder="github-login"
                      error={verdict.severity === 'error'}
                      onKeyDown={e => {
                        if (e.key === 'Enter') submitAdd();
                      }}
                      inputProps={{
                        ...params.inputProps,
                        'aria-label': `Add member to ${team.slug}`,
                      }}
                    />
                  )}
                />
                <Tooltip title={addHint}>
                  {/* span: a disabled button fires no events, so the tooltip
                      needs a live target. */}
                  <span>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={submitAdd}
                      disabled={!typed || verdict.blocking || checking}
                    >
                      Add member
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </div>
          )}
          {/* Verdict for the typed login: an account that doesn't exist blocks
              the add, everything else is advisory. Without this the typo
              survives the cart and fails the WHOLE batch at dry-run, naming no
              row. */}
          {canEdit && (typed !== '' || checking) && (
            <Typography
              className={classes[VERDICT_STYLE[verdict.severity]]}
              role={verdict.severity === 'error' ? 'alert' : 'status'}
            >
              {checking ? `Checking ${typed} with GitHub…` : verdict.text}
              {/* Which role they will actually land in. The role is derived, so
                  it can be stated before staging rather than chosen -- and
                  saying it here is the difference between "why is this person a
                  maintainer?" and knowing in advance. */}
              {!checking && !verdict.blocking && typed !== '' && (
                <>
                  {' '}
                  {orgOwners.has(typed)
                    ? 'Joins as a maintainer — they are an org owner.'
                    : 'Joins as a member.'}
                </>
              )}
            </Typography>
          )}
          {team.members.length === 0 && ghostAdds.length === 0 ? (
            <Typography className={classes.empty}>No members listed.</Typography>
          ) : (
            roleGroups.map(group => (
              <div className={classes.rosterGroup} key={group.label}>
                <h4 className={classes.rosterGroupLabel}>
                  {group.label}
                  <span className={classes.rosterGroupCount}>
                    {group.people.length}
                  </span>
                </h4>
                <ul
                  className={classes.rosterFlex}
                  aria-label={`${team.slug} ${group.label.toLowerCase()}`}
                >
                  {group.people.map((p, i) => (
                    <MemberRow
                      key={p.key}
                      login={p.login}
                      isMaintainer={p.isMaintainer}
                      index={i}
                      hit={
                        Boolean(query) && p.login.toLowerCase().includes(query)
                      }
                      removable={p.removable}
                      pending={p.pending}
                      onRemove={() => onMember(team, p.login, 'remove')}
                      onUndo={() =>
                        p.stagedId !== undefined && pending.unstage(p.stagedId)
                      }
                    />
                  ))}
                </ul>
              </div>
            ))
          )}
          {/* One sentence, not the old paragraph. The second half explained that
              repo access is team-wide, which the Access zone now states as its
              own labelled value; repeating it under every expanded team was a
              wall of text that said what the structure already says. */}
          {maintainers.size > 0 && (
            <Typography className={classes.memberLegend}>
              <strong>Maintainers</strong> are org owners. The role is derived
              from the org owners list, not granted per team, so it cannot be
              changed here — promoting someone would make them a maintainer of
              every team they belong to.
            </Typography>
          )}
        </div>
      </Collapse>
    </div>
  );
}

// Inline click-to-edit text for a team's description (Enter saves, Esc cancels).
function InlineDescription({
  value,
  onSave,
}: {
  value: string;
  onSave: (next: string) => void;
}) {
  const classes = useStyles();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = () => {
    const next = draft.trim();
    if (next && next !== value) onSave(next);
    setEditing(false);
  };

  if (editing) {
    return (
      <TextField
        className={classes.descEdit}
        size="small"
        variant="outlined"
        label="Description"
        // autoFocus, NOT an inline inputRef callback. React re-invokes a ref
        // callback on every render (the arrow function is a new identity each
        // time), so `el => el.focus()` re-focused this input on EVERY keystroke
        // -- which reset the caret to the end of the text and fought any
        // selection. autoFocus fires once, when the editor mounts.
        //
        // The no-autofocus rule targets focus stolen on page load. This editor
        // only exists because the user just clicked "Edit description", so the
        // focus is the direct result of their action -- the disclosure-triggered
        // exception the rule allows for.
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setDraft(value);
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <span
      className={classes.descShow}
      role="button"
      tabIndex={0}
      aria-label="Edit description"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setDraft(value);
          setEditing(true);
        }
      }}
    >
      {value || <span className={classes.descEmpty}>No description</span>}
      <EditIcon className={`${classes.editPencil} editPencil`} />
    </span>
  );
}

// The editable detail strip shown when a team is expanded.
function TeamDetails({
  team,
  teamSlugs,
  org,
  pendingFields,
  onEditConfig,
  onPermission,
  onGrantRepoAccess,
  onRevokeRepoAccess,
  onDelete,
}: {
  /** Already overlaid with staged edits by the caller -- see overlayTeam. */
  team: Team;
  teamSlugs: string[];
  org: string;
  /** Config fields whose value comes from the cart rather than the server. */
  pendingFields: ReadonlySet<string>;
  onEditConfig: (team: Team, patch: ConfigPatch) => void;
  onPermission: (team: Team, permission: string) => void;
  onGrantRepoAccess: (team: Team, permission: string, repos: string[]) => void;
  onRevokeRepoAccess: (team: Team, repo: string) => void;
  onDelete: (team: Team) => void;
}) {
  const classes = useStyles();
  const pending = usePending();
  const [editing, setEditing] = useState(false);
  // Which bundle the picker edits. Picks stage straight into the cart (no
  // Stage button, same as a member add); the pill tray below renders the
  // cart's bundles, so nothing here can drift from what Review will apply.
  const [grantPerm, setGrantPerm] = useState('push');
  const grantBundles = pending.grantEdits.get(team.slug) ?? [];
  // Selected-state spans every bundle: a repo staged under push must still
  // show its checkmark while the select says pull, or re-picking it would
  // stage the same repo twice under conflicting permissions.
  const stagedRepos = new Set(grantBundles.flatMap(b => b.repos));
  // Fetched only once someone actually edits -- most expansions never do.
  const grantable = useGrantableRepos(org, editing);

  return (
    <Box className={classes.details}>
      <div className={classes.zoneHeader}>
        <h4 className={classes.zoneLabel}>Access</h4>
        <div className={classes.zoneActions}>
          <Button
            size="small"
            startIcon={editing ? undefined : <EditIcon style={{ fontSize: 14 }} />}
            onClick={() => setEditing(e => !e)}
            aria-label={
              editing
                ? `Finish editing ${team.slug} access`
                : `Edit ${team.slug} access`
            }
          >
            {editing ? 'Done' : 'Edit'}
          </Button>
          {/* Last in the cluster, and last in tab order. */}
          <DeleteTeamButton team={team} onDelete={onDelete} />
        </div>
      </div>

      <div>
        <Typography className={classes.detailLabel}>Description</Typography>
        <InlineDescription
          value={team.description ?? ''}
          onSave={d => onEditConfig(team, { description: d })}
        />
      </div>

      {editing ? (
        <div className={classes.configRow}>
          <Autocomplete
            freeSolo
            className={classes.detailField}
            options={teamSlugs.filter(s => s !== team.slug)}
            inputValue={team.parentTeamId ?? ''}
            onChange={(_e, v) =>
              onEditConfig(team, { parentTeamId: (v as string) || undefined })
            }
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                variant="outlined"
                label="Parent team"
                placeholder="top level"
                // Without a forced shrink the label stays centred over an empty
                // field, which both hides the placeholder and (on the select
                // below) paints the label straight over the value text.
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              />
            )}
          />
          <TextField
            select
            className={classes.detailField}
            size="small"
            variant="outlined"
            label="Privacy"
            value={team.privacy ?? 'closed'}
            onChange={e => onEditConfig(team, { privacy: e.target.value })}
            helperText={pendingFields.has('privacy') ? 'staged' : undefined}
            FormHelperTextProps={{ className: classes.pendingHint }}
          >
            {PRIVACIES.map(p => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            className={classes.defaultAccessField}
            size="small"
            variant="outlined"
            label="Default access (all repos)"
            value={team.permission ?? ''}
            onChange={e => onPermission(team, e.target.value)}
            /* The blast radius lives ON the control. "Repo access" gave no hint
               that it meant every categorized repo, current and future -- which
               is how a one-repo request became an org-wide grant in a planned PR. */
            helperText={
              pendingFields.has('permission')
                ? 'staged'
                : 'every categorized repo, current and future'
            }
            FormHelperTextProps={{ className: classes.pendingHint }}
            // Required with displayEmpty: MUI v4 shrinks the label from the
            // VALUE, and an empty value left it centred on top of the
            // "none granted" text -- the two overlapped.
            InputLabelProps={{ shrink: true }}
            // A placeholder option so a team that grants nothing shows words
            // rather than a blank box. It is disabled because REMOVING access
            // isn't supported here: the backend's setTeamPermission rejects
            // anything outside pull/triage/push/maintain/admin, so an enabled
            // "none" would stage a change that fails at dry-run.
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              none granted
            </MenuItem>
            {PERMISSIONS.map(p => (
              <MenuItem key={p} value={p}>
                {/* The API value is shown only where it DIFFERS from GitHub's
                    name -- "Read (pull)" and "Write (push)" earn the gloss,
                    "Triage (triage)" is just noise. */}
                {PERMISSION_LABELS[p] && PERMISSION_LABELS[p].toLowerCase() !== p
                  ? `${PERMISSION_LABELS[p]} (${p})`
                  : PERMISSION_LABELS[p] ?? p}
              </MenuItem>
            ))}
          </TextField>
        </div>
      ) : null}
      {editing ? (
        <div>
          <Typography className={classes.detailLabel}>
            Scoped access — named repos only
          </Typography>
          {(team.scopedGrants ?? []).length > 0 && (
            <div className={classes.grantChips}>
              {team.scopedGrants.map(g => {
                const label = `${g.repo} · ${permGloss(g.permission)}`;
                const revokeId = pending.grantRevokes
                  .get(team.slug)
                  ?.get(g.repo);
                if (revokeId !== undefined) {
                  return (
                    <Chip
                      key={`${g.repo}:${g.permission}`}
                      size="small"
                      label={label}
                      className={classes.grantChipRevoked}
                      onDelete={() => pending.unstage(revokeId)}
                      deleteIcon={
                        <UndoIcon
                          titleAccess={`Undo revoking ${g.repo} from ${team.slug}`}
                        />
                      }
                    />
                  );
                }
                if (g.source === 'computed') {
                  return (
                    <Tooltip
                      key={`${g.repo}:${g.permission}`}
                      title="Produced by a plan-time expression in the Terraform — Backstage cannot edit it; change the expression or its source list by hand"
                    >
                      <Chip size="small" variant="outlined" label={label} />
                    </Tooltip>
                  );
                }
                // A staged bundle covering this repo means the grant below is
                // outgoing -- the writer removes it when the new one lands.
                const replaced = stagedRepos.has(g.repo);
                return (
                  <Chip
                    key={`${g.repo}:${g.permission}`}
                    size="small"
                    variant="outlined"
                    label={label}
                    className={replaced ? classes.grantChipReplaced : undefined}
                    title={
                      replaced ? 'will be replaced by the staged grant' : undefined
                    }
                    onDelete={() => onRevokeRepoAccess(team, g.repo)}
                    deleteIcon={
                      <CloseIcon
                        titleAccess={`Revoke ${team.slug} access to ${g.repo}`}
                      />
                    }
                  />
                );
              })}
            </div>
          )}
          <div className={classes.configRow}>
            <Autocomplete<GrantableRepo, true, false, false>
              multiple
              className={classes.grantPicker}
              size="small"
              options={grantable.data?.repos ?? []}
              /* Uncategorized repos are PRESENT AND DISABLED, not omitted: someone
                 typing "svc-test" learns why it is refused instead of concluding
                 the field is broken. The disabling is advisory UI -- the backend
                 re-validates every repo against the categories at dry-run. */
              getOptionDisabled={o => !o.grantable}
              getOptionLabel={o => o.name}
              getOptionSelected={(o, v) => o.name === v.name}
              // Picks collect in the pill tray below, not as tags inside the
              // input -- so the box stays a search box however many repos are
              // queued, and the popup stays open for fluid multi-picking.
              renderTags={() => null}
              disableCloseOnSelect
              classes={{ option: classes.grantOptionDisabled }}
              renderOption={o => (
                <div>
                  <Typography variant="body2">{o.name}</Typography>
                  {!o.grantable && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className={classes.grantReason}
                    >
                      not in a repository category — grant by hand
                    </Typography>
                  )}
                </div>
              )}
              value={(grantable.data?.repos ?? []).filter(o =>
                stagedRepos.has(o.name),
              )}
              onChange={(_e, v) => {
                for (const b of rebundlePicks(
                  grantBundles,
                  grantPerm,
                  v.map(o => o.name),
                )) {
                  onGrantRepoAccess(team, b.permission, b.repos);
                }
              }}
              disabled={!grantable.data}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  variant="outlined"
                  label="Repositories"
                  error={Boolean(grantable.error)}
                  helperText={
                    grantable.error ??
                    (grantable.data
                      ? 'greyed repos are not in a repository category; those need a team_repository_attachments grant written by hand'
                      : 'loading repositories…')
                  }
                />
              )}
            />
            <TextField
              select
              className={classes.detailField}
              size="small"
              variant="outlined"
              label="Permission"
              value={grantPerm}
              onChange={e => setGrantPerm(e.target.value)}
            >
              {PERMISSIONS.map(p => (
                <MenuItem key={p} value={p}>
                  {PERMISSION_LABELS[p] && PERMISSION_LABELS[p].toLowerCase() !== p
                    ? `${PERMISSION_LABELS[p]} (${p})`
                    : PERMISSION_LABELS[p] ?? p}
                </MenuItem>
              ))}
            </TextField>
          </div>
          {grantBundles.length > 0 && (
            <div
              className={classes.grantDraft}
              aria-label={`Staged scoped access for ${team.slug}`}
            >
              {grantBundles.flatMap(b =>
                b.repos.map(r => (
                  <Chip
                    key={`${b.permission}:${r}`}
                    size="small"
                    /* Every pill names its captured permission, in the same
                       gloss the pickers use -- a grant is repo AND level, and
                       half the fact should not depend on where the Permission
                       select happens to sit. */
                    label={`${r} · ${permGloss(b.permission)}`}
                    className={classes.grantDraftPill}
                    onDelete={() =>
                      onGrantRepoAccess(
                        team,
                        b.permission,
                        b.repos.filter(x => x !== r),
                      )
                    }
                    deleteIcon={
                      <CloseIcon titleAccess={`Unstage ${r} (${b.permission})`} />
                    }
                  />
                )),
              )}
            </div>
          )}
        </div>
      ) : (
        <dl className={classes.attrList}>
          <div className={classes.attrRow}>
            <dt className={classes.attrKey}>Parent team</dt>
            {team.parentTeamId ? (
              <dd className={classes.attrValue}>{team.parentTeamId}</dd>
            ) : (
              <dd className={classes.attrValueEmpty}>top level</dd>
            )}
          </div>
          <div className={classes.attrRow}>
            <dt className={classes.attrKey}>Privacy</dt>
            <dd className={classes.attrValue}>{team.privacy ?? 'closed'}</dd>
          </div>
          <div className={classes.attrRow}>
            <dt className={classes.attrKey}>Default access</dt>
            {team.permission ? (
              <dd className={classes.attrValue}>{team.permission}</dd>
            ) : (
              <dd className={classes.attrValueEmpty}>none granted</dd>
            )}
          </div>
          {(team.scopedGrants ?? []).length > 0 && (
            <div className={classes.attrRow}>
              <dt className={classes.attrKey}>Scoped access</dt>
              <dd className={classes.attrValue}>
                {team.scopedGrants
                  .map(g => `${g.repo} (${g.permission})`)
                  .join(', ')}
              </dd>
            </div>
          )}
        </dl>
      )}

    </Box>
  );
}

// Toggles between staging a delete and undoing the staged delete.
function DeleteTeamButton({
  team,
  onDelete,
}: {
  team: Team;
  onDelete: (team: Team) => void;
}) {
  const classes = useStyles();
  const pending = usePending();
  const removeId = pending.removedTeams.get(team.slug);
  if (removeId !== undefined) {
    return (
      <Button
        size="small"
        className={classes.deleteBtn}
        startIcon={<UndoIcon style={{ fontSize: 15 }} />}
        onClick={() => pending.unstage(removeId)}
      >
        Undo delete
      </Button>
    );
  }
  return (
    <Button
      size="small"
      className={classes.deleteBtn}
      onClick={() => onDelete(team)}
    >
      Delete team
    </Button>
  );
}

// Inline new-team form (root-level or as a sub-team of `parentTeamId`).
/**
 * A staged new team, previewed in the tree.
 *
 * Deliberately NOT expandable: it does not exist server-side, so there is
 * nothing to fetch or edit inside it -- its members and description live in
 * the staged change (visible in full in the cart). The row's job is to answer
 * "did my creation register?" where the user was looking when they staged it.
 */
function GhostTeamRow({
  ghost,
  onEdit,
}: {
  ghost: GhostTeam;
  onEdit: (g: GhostTeam) => void;
}) {
  const classes = useStyles();
  const { unstage } = usePending();
  const memberCount = ghost.members.length;
  return (
    <div className={classes.ghostTeamRow}>
      <span className={classes.ghostTeamIndent} aria-hidden />
      <span className={classes.slug}>{ghost.slug}</span>
      <Chip
        className={`${classes.stateChip} ${classes.stateChipNew}`}
        classes={{ label: classes.stateChipLabel }}
        size="small"
        label="new"
      />
      {ghost.description && (
        <span className={classes.desc}>{ghost.description}</span>
      )}
      <span className={classes.ghostTeamMeta}>
        {memberCount === 0
          ? 'no members yet'
          : `${memberCount} member${memberCount === 1 ? '' : 's'}`}
      </span>
      {/* Amend, not just discard: a staged team is a form submission, so it
          can be reopened and corrected without retyping it. */}
      <Tooltip title="Edit this staged team">
        <IconButton
          className={classes.pendingActionBtn}
          size="small"
          aria-label={`Edit staged team ${ghost.slug}`}
          onClick={() => onEdit(ghost)}
        >
          <EditIcon style={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Undo creating this team">
        <IconButton
          className={classes.pendingActionBtn}
          size="small"
          aria-label={`Undo create team ${ghost.slug}`}
          onClick={() => unstage(ghost.id)}
        >
          <UndoIcon style={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </div>
  );
}

/**
 * Create a team -- and AMEND one that is already staged.
 *
 * A staged team is just this form's submission sitting in the cart, so editing
 * it reopens the form pre-filled and re-submitting REPLACES the staged change
 * (unstage the old, stage the amended). That keeps one source of truth: there
 * is no second editing surface that could disagree with the cart, and no need
 * to stage member/config ops against a team the server has never heard of.
 *
 * `editing` arrives via a remount (the caller keys on the staged id), which is
 * what lets the initial state below read from it.
 */
function NewTeamRow({
  org,
  parentTeamId,
  teamSlugs,
  orgLogins,
  onStage,
  editing,
  onEditDone,
}: {
  org: string;
  parentTeamId?: string;
  teamSlugs: string[];
  orgLogins: string[];
  onStage: (label: string, change: Change) => void;
  /** The staged team being amended, if any. */
  editing?: GhostTeam;
  /** Called when an amend finishes or is abandoned, so the caller can clear it. */
  onEditDone?: () => void;
}) {
  const classes = useStyles();
  const { unstage, addedTeams } = usePending();
  const [adding, setAdding] = useState(Boolean(editing));
  const [slug, setSlug] = useState(editing?.slug ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [parent, setParent] = useState(
    editing?.parentTeamId ?? parentTeamId ?? '',
  );
  const [members, setMembers] = useState<string[]>(editing?.members ?? []);

  // Slugs already taken: teams that exist, plus teams already STAGED (a second
  // creation of the same slug is a no-op the backend silently discards, so
  // catching it here is the difference between "fix the typo" and "wonder why
  // the PR did nothing").
  const stagedSlugs = useMemo(() => {
    const out = new Set<string>();
    for (const list of addedTeams.values()) {
      for (const g of list) if (g.id !== editing?.id) out.add(g.slug);
    }
    return out;
  }, [addedTeams, editing?.id]);

  const trimmedSlug = slug.trim();
  const trimmedDesc = description.trim();
  const problem = teamProblem({
    slug,
    description,
    parent,
    members,
    existingSlugs: teamSlugs,
    stagedSlugs,
  });
  // Every member is checked against GitHub. An account that does not exist
  // blocks staging (it can only be a typo); "exists but not in this org yet" is
  // warned and allowed, because a brand-new hire legitimately isn't in the org
  // list yet and refusing them would break the most common reason to make a team.
  const { checks, checking } = useGithubLogins(members, org);
  const memberVerdicts = members
    .map(m => m.trim())
    .filter(Boolean)
    .map(m => ({ login: m, verdict: loginVerdict(checks.get(m.toLowerCase()), org) }));
  const badLogins = memberVerdicts.filter(v => v.verdict.blocking);
  const warnLogins = memberVerdicts.filter(v => v.verdict.severity === 'warn');
  const memberProblem =
    badLogins.length > 0
      ? badLogins.map(b => b.verdict.text).join(' ')
      : null;
  const canSubmit =
    Boolean(trimmedSlug) &&
    Boolean(trimmedDesc) &&
    !problem &&
    !memberProblem &&
    !checking;

  // The form's one message line: a blocking problem wins, then the in-flight
  // check, then the advisory warnings.
  const blockingProblem = problem ?? memberProblem;
  let formSeverity: LoginVerdict['severity'] = 'warn';
  let formMessage = warnLogins.map(w => w.verdict.text).join(' ');
  if (blockingProblem) {
    formSeverity = 'error';
    formMessage = blockingProblem;
  } else if (checking) {
    formSeverity = 'ok';
    formMessage = 'Checking members with GitHub…';
  }

  // Why a disabled submit is disabled, in fix order.
  let submitHint = '';
  if (!canSubmit) {
    if (problem) submitHint = problem;
    else if (memberProblem) submitHint = memberProblem;
    else if (!trimmedSlug) submitHint = 'Enter a team slug';
    else if (!trimmedDesc)
      submitHint = 'Enter a description — Terraform stores one per team';
    else submitHint = 'Checking members with GitHub…';
  }

  const reset = () => {
    setSlug('');
    setDescription('');
    setParent(parentTeamId ?? '');
    setMembers([]);
    setAdding(false);
    onEditDone?.();
  };
  const submit = () => {
    // Replace, don't duplicate: the amended change supersedes the staged one.
    if (editing) unstage(editing.id);
    onStage(`Create team ${slug.trim()} in ${org}`, {
      op: 'addTeam',
      org,
      slug: slug.trim(),
      description: description.trim(),
      parentTeamId: parent.trim() || undefined,
      // GitHub's own spelling where we resolved it, so one person can't land in
      // the Terraform list twice under two casings.
      members: members
        .map(s => s.trim())
        .filter(Boolean)
        .map(s => checks.get(s.toLowerCase())?.canonicalLogin ?? s),
    });
    reset();
  };

  if (!adding) {
    return (
      <Button
        className={classes.addLink}
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setAdding(true)}
      >
        {parentTeamId ? 'New sub-team' : 'New team'}
      </Button>
    );
  }
  return (
    <Box className={classes.newTeamForm}>
      <TextField
        size="small"
        variant="outlined"
        label="Team slug"
        /**
         * autoFocus, NOT an inline inputRef callback.
         *
         * `inputRef={el => el?.focus()}` is a NEW function on every render, so
         * React detached and re-attached the ref after every state change --
         * and the callback re-focused THIS field. Typing one character into
         * Description, Parent or Members re-rendered the form and snapped the
         * caret back here, making those fields accept exactly one character.
         * autoFocus focuses the slug once, when the form opens, which is all
         * that was ever intended.
         *
         * The no-autofocus rule targets focus stolen on page load. This form
         * only mounts because the user just clicked "New team", so the focus
         * follows their action -- the disclosure-triggered exception.
         */
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        placeholder="m42-new-team"
        value={slug}
        onChange={e => setSlug(e.target.value)}
        error={Boolean(problem)}
        // Enter submits when the form is valid -- the critique's power-user
        // finding: every other field in this page commits on Enter.
        onKeyDown={e => {
          if (e.key === 'Enter' && canSubmit) submit();
          if (e.key === 'Escape') reset();
        }}
      />
      <TextField
        size="small"
        variant="outlined"
        label="Description"
        required
        value={description}
        onChange={e => setDescription(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && canSubmit) submit();
          if (e.key === 'Escape') reset();
        }}
      />
      {/**
        * Parent. `value` is controlled ALONGSIDE `inputValue`, and 'reset'
        * input changes are ignored -- both guard the same MUI behaviour.
        *
        * With only `inputValue` controlled, useAutocomplete's selection value
        * stays uncontrolled (null) and its reset effect recomputes the input
        * text FROM that null -- firing onInputChange(_, '', 'reset') on mount.
        * That wiped a pre-filled parent: amending a staged sub-team reopened
        * with Parent empty, so "Update staged team" silently relocated the team
        * to the root, and "New sub-team" was one blur away from the same. With
        * `value={parent}` the reset computes to the string we already hold, so
        * there is nothing to clear.
        */}
      <Autocomplete
        freeSolo
        style={{ minWidth: 160 }}
        options={teamSlugs}
        value={parent}
        inputValue={parent}
        onChange={(_e, v) => setParent(((v as string) ?? '').trim())}
        onInputChange={(_e, v, reason) => {
          if (reason !== 'reset') setParent(v);
        }}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            label="Parent (optional)"
          />
        )}
      />
      <Autocomplete
        multiple
        freeSolo
        style={{ minWidth: 220 }}
        options={orgLogins}
        value={members}
        onChange={(_e, v) => setMembers(v as string[])}
        renderInput={params => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            label="Members (optional)"
          />
        )}
      />
      <Button onClick={reset} size="small">
        Cancel
      </Button>
      {/* ONE message line, full width beneath the fields.
          Per-field helperText made the fields unequal in height, and with the
          row centred the one field without a helper (Parent) sat visibly lower.
          A shared line keeps every field the same height no matter how long the
          message is -- and gives a wrapping error room instead of stretching
          one field. */}
      {formMessage !== '' && (
        <Typography
          className={classes[VERDICT_STYLE[formSeverity]]}
          role={formSeverity === 'error' ? 'alert' : 'status'}
        >
          {formMessage}
        </Typography>
      )}
      <Tooltip title={submitHint}>
        {/* span: a disabled button fires no events, so the tooltip needs a live
            target. A mute disabled button is the thing this replaces. */}
        <span>
          <Button
            onClick={submit}
            size="small"
            color="primary"
            variant="contained"
            disabled={!canSubmit}
          >
            {editing ? 'Update staged team' : 'Add to review'}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
}

// A team branch: header (expand) -> details + child teams + members + new-subteam.
function TeamNode({
  team,
  depth,
  childrenOf,
  isRoot,
  teamSlugs,
  orgLogins,
  orgOwners,
  org,
  query,
  shouldRender,
  forceOpen,
  memberHit,
  onMember,
  onEditConfig,
  onPermission,
  onGrantRepoAccess,
  onRevokeRepoAccess,
  onDelete,
  onStage,
  editTeam,
  onEditTeam,
}: {
  team: Team;
  depth: number;
  childrenOf: Map<string, Team[]>;
  isRoot: boolean;
  teamSlugs: string[];
  orgLogins: string[];
  orgOwners: ReadonlySet<string>;
  org: string;
  query: string;
  shouldRender: (t: Team) => boolean;
  forceOpen: (t: Team) => boolean;
  memberHit: (t: Team) => boolean;
  onMember: (team: Team, login: string, action: 'add' | 'remove') => void;
  onEditConfig: (team: Team, patch: ConfigPatch) => void;
  onPermission: (team: Team, permission: string) => void;
  onGrantRepoAccess: (team: Team, permission: string, repos: string[]) => void;
  onRevokeRepoAccess: (team: Team, repo: string) => void;
  onDelete: (team: Team) => void;
  onStage: (label: string, change: Change) => void;
  editTeam: GhostTeam | null;
  onEditTeam: (g: GhostTeam | null) => void;
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const expanded = forceOpen(team) || open;
  const kids = (childrenOf.get(team.slug) ?? []).filter(shouldRender);
  const pending = usePending();
  // Staged sub-teams for this parent, excluding the one currently being amended
  // in the form below (it would otherwise appear twice).
  const stagedKids = (pending.addedTeams.get(team.slug) ?? []).filter(
    g => g.id !== editTeam?.id,
  );
  const willDelete = pending.removedTeams.has(team.slug);
  const edited = pending.editedTeams.has(team.slug);

  return (
    // HONEST SEMANTICS, not a tree.
    // This used to claim role="treeitem" (with aria-selected and aria-level)
    // inside a role="tree" -- a contract that promises arrow-key navigation,
    // roving tabindex and a selection model, none of which exist here. What it
    // actually is: nested DISCLOSURE buttons, which are already fully
    // keyboard-operable by Tab + Enter/Space and announce their own
    // expanded/collapsed state. Claiming the richer role only misled screen
    // readers about which keys would work.
    <div>
      <button
        type="button"
        className={classes.teamRow}
        aria-expanded={expanded}
        // Without this the row's own text concatenates into a run-on --
        // "m42-onlineM42 Online - parent team...push30 members" -- because the
        // chips and spans carry no separators. Spelled out here instead.
        aria-label={teamRowLabel(team, { willDelete, edited })}
        onClick={() => setOpen(o => !o)}
      >
        <ChevronRightIcon
          fontSize="small"
          className={`${classes.twisty}${expanded ? ` ${classes.twistyOpen}` : ''}`}
        />
        <span
          className={`${classes.slug}${depth > 0 ? ` ${classes.slugNested}` : ''} teamSlug${
            willDelete ? ` ${classes.slugStrike}` : ''
          }`}
        >
          {team.slug}
        </span>
        {willDelete && (
          <Chip
            className={`${classes.stateChip} ${classes.stateChipDelete}`}
            classes={{ label: classes.stateChipLabel }}
            size="small"
            label="will delete"
          />
        )}
        {edited && !willDelete && (
          <Tooltip title="This team has uncommitted edits">
            <span className={classes.editedDot} />
          </Tooltip>
        )}
        {isRoot && team.parentTeamId && (
          <Chip
            className={classes.parentChip}
            size="small"
            variant="outlined"
            label={`↳ ${team.parentTeamId}`}
          />
        )}
        {team.description && (
          <span className={classes.desc}>{team.description}</span>
        )}
        {team.permission && (
          <Chip
            className={classes.permChip}
            size="small"
            label={team.permission}
          />
        )}
        <span className={classes.count}>
          {team.members.length}{' '}
          {team.members.length === 1 ? 'member' : 'members'}
        </span>
      </button>
      <Collapse in={expanded} unmountOnExit>
        <div
          className={`${classes.group}${
            depth > 0 ? ` ${classes.groupNested}` : ''
          }`}
        >
          <TeamDetails
            // Overlaid, not raw: the fields must show what the cart will apply.
            team={overlayTeam(team, pending.configEdits.get(team.slug))}
            teamSlugs={teamSlugs}
            pendingFields={pendingFieldsOf(pending.configEdits.get(team.slug))}
            onEditConfig={onEditConfig}
            onPermission={onPermission}
            onGrantRepoAccess={onGrantRepoAccess}
            onRevokeRepoAccess={onRevokeRepoAccess}
            org={org}
            onDelete={onDelete}
          />
          <MembersDisclosure
            team={team}
            orgLogins={orgLogins}
            orgOwners={orgOwners}
            org={org}
            query={query}
            forceOpen={memberHit(team)}
            onMember={onMember}
          />
          {(kids.length > 0 || stagedKids.length > 0) && (
            <div className={classes.subTeamsLabel}>
              Sub-teams
              <span className={classes.rosterGroupCount}>
                {kids.length + stagedKids.length}
              </span>
            </div>
          )}
          {kids.map(child => (
            <TeamNode
              key={child.slug}
              team={child}
              depth={depth + 1}
              childrenOf={childrenOf}
              isRoot={false}
              teamSlugs={teamSlugs}
              orgLogins={orgLogins}
              orgOwners={orgOwners}
              org={org}
              query={query}
              shouldRender={shouldRender}
              forceOpen={forceOpen}
              memberHit={memberHit}
              onMember={onMember}
              onEditConfig={onEditConfig}
              onPermission={onPermission}
              onGrantRepoAccess={onGrantRepoAccess}
              onRevokeRepoAccess={onRevokeRepoAccess}
              onDelete={onDelete}
              onStage={onStage}
              editTeam={editTeam}
              onEditTeam={onEditTeam}
            />
          ))}
          {/* Staged sub-teams, previewed where they will live. */}
          {stagedKids.map(g => (
            <GhostTeamRow key={`ghost-${g.id}`} ghost={g} onEdit={onEditTeam} />
          ))}
          <NewTeamRow
            key={
              editTeam?.parentTeamId === team.slug
                ? `edit-${editTeam.id}`
                : 'new'
            }
            org={org}
            parentTeamId={team.slug}
            teamSlugs={teamSlugs}
            orgLogins={orgLogins}
            onStage={onStage}
            editing={
              editTeam?.parentTeamId === team.slug ? editTeam : undefined
            }
            onEditDone={() => onEditTeam(null)}
          />
          {team.memberSource && (
            <div className={classes.sourceNote}>
              source: local.{team.memberSource}
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
}

// Right-column aside: who can administer teams, and how to grant access.
// Populated from the backend gate config (m42.teamAdmin) via /access.
function AccessInfoCard({
  groups,
  users,
}: {
  groups: string[];
  users: string[];
}) {
  const classes = useStyles();
  const openToAll = groups.includes('*');
  if (openToAll) {
    return (
      <InfoCard title="Access">
        <Typography className={classes.accessIntro}>
          Team Admin is open to all m42 members. Anyone signed in who belongs to
          a GitHub team can view and propose team changes. Every change is still
          a reviewed PR to <code>.github-private</code> and applies only on{' '}
          <code>atlantis apply</code>.
        </Typography>
      </InfoCard>
    );
  }
  return (
    <InfoCard title="Access">
      <Typography className={classes.accessIntro}>
        Team Admin is restricted. A person has access if they belong to any of
        these groups, or if their GitHub login is on the allow-list.
      </Typography>

      <Typography className={classes.accessLabel}>Allowed groups</Typography>
      <div className={classes.accessChips}>
        {groups.length > 0 ? (
          groups.map(g => (
            <Link key={g} to={`/catalog/default/group/${g}`}>
              <Chip
                className={classes.accessGroupChip}
                size="small"
                clickable
                label={g}
              />
            </Link>
          ))
        ) : (
          <span className={classes.accessNone}>none configured</span>
        )}
      </div>

      <Typography className={classes.accessLabel}>Allowed users</Typography>
      <div className={classes.accessChips}>
        {users.length > 0 ? (
          users.map(u => (
            <span key={u} className={classes.accessUser}>
              {u}
            </span>
          ))
        ) : (
          <span className={classes.accessNone}>none</span>
        )}
      </div>

      <Typography className={classes.accessLabel}>Granting access</Typography>
      <Typography className={classes.accessHow}>
        Add the person to one of the groups above — you can do that here in Team
        Admin — or add their GitHub login under <code>m42.teamAdmin.users</code>{' '}
        in <code>app-config</code> (ops change, no rebuild).
      </Typography>
    </InfoCard>
  );
}

export const TeamAdminPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const { allowed, groups: accessGroups, users: accessUsers } =
    useTeamAdminAccess();

  const [data, setData] = useState<TeamsResponse | null>(null);
  const [reloading, setReloading] = useState(false);
  // When the data on screen was fetched. Shown beside Reload because the
  // backend caches each org's teams file for two minutes -- without a stamp, a
  // reload inside that window returns identical rows and reads as a dead button.
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const [org, setOrg] = useState(ORG_ORDER[0]);
  const [pendingChanges, setPendingChanges] = useState<StagedChange[]>(restoreCart);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [q, setQ] = useState('');
  /**
   * The staged team currently being amended, if any.
   *
   * Held here rather than per-row because the form that reopens must be the one
   * at the ghost's OWN level (root, or under its parent team) -- each
   * NewTeamRow claims it only when the parent matches, so an amend never opens
   * a form in the wrong place.
   */
  const [editTeam, setEditTeam] = useState<GhostTeam | null>(null);
  // Seeded past any restored ids so a re-staged change cannot collide with a
  // restored one (unstage() keys on id).
  const idRef = useRef(0);


  /**
   * The cart survives a reload.
   *
   * It holds the riskiest state in the system -- admin intent that has not
   * shipped yet -- and it used to be bare React state: fifteen staged edits and
   * one reflexive Cmd-R lost everything, silently. sessionStorage (not local)
   * on purpose: the cart belongs to this tab's working session, so a stale cart
   * cannot ambush a different tab or tomorrow's visit. Writes are best-effort;
   * a storage failure (private mode, quota) must never break staging, so the
   * in-memory cart stays the source of truth and persistence is a bonus.
   */
  useEffect(() => {
    for (const s of pendingChanges) {
      if (s.id > idRef.current) idRef.current = s.id;
    }
    try {
      if (pendingChanges.length) {
        sessionStorage.setItem(CART_KEY, JSON.stringify(pendingChanges));
      } else {
        sessionStorage.removeItem(CART_KEY);
      }
    } catch {
      /* persistence is a bonus; the in-memory cart is the source of truth */
    }
  }, [pendingChanges]);

  /**
   * Belt to that braces: the browser's own "leave site?" prompt while the cart
   * is non-empty. Restoration covers a reload, but a closed tab takes
   * sessionStorage with it -- this is the last line before that.
   */
  useEffect(() => {
    if (!pendingChanges.length) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Legacy assignment: Chrome/Safari still gate the prompt on returnValue.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pendingChanges.length]);

  // Stage a change into the cart (the only path to a PR is Review -> Save).
  const stage = (label: string, change: Change) => {
    idRef.current += 1;
    setPendingChanges(p => [...p, { id: idRef.current, label, change }]);
  };
  const unstage = useCallback(
    (id: number) => setPendingChanges(p => p.filter(s => s.id !== id)),
    [],
  );

  const load = useCallback(async () => {
    try {
      const base = await discoveryApi.getBaseUrl('team-admin');
      const res = await fetchApi.fetch(`${base}/teams`);
      if (!res.ok) throw new Error(String(res.status));
      setData((await res.json()) as TeamsResponse);
      setLoadedAt(Date.now());
      // A reload that succeeds clears a previous failure, so one bad fetch
      // doesn't strand the page in its error state for the session.
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, [discoveryApi, fetchApi]);

  const reload = useCallback(async () => {
    setReloading(true);
    try {
      await load();
    } finally {
      setReloading(false);
    }
  }, [load]);

  useEffect(() => {
    if (allowed === true) load();
  }, [allowed, load]);

  const onMember = (team: Team, login: string, action: 'add' | 'remove') =>
    stage(
      `${action === 'add' ? 'Add' : 'Remove'} ${login} ${
        action === 'add' ? 'to' : 'from'
      } ${team.slug}`,
      {
        op: action === 'add' ? 'addMember' : 'removeMember',
        org,
        team: team.slug,
        login,
      },
    );
  const onGrantRepoAccess = (team: Team, permission: string, repos: string[]) =>
    setPendingChanges(p =>
      upsertGrant(p, {
        org,
        slug: team.slug,
        permission,
        repos,
        nextId: () => {
          idRef.current += 1;
          return idRef.current;
        },
      }),
    );
  const onRevokeRepoAccess = (team: Team, repo: string) =>
    setPendingChanges(p => {
      // Idempotent: a second click on an already-staged revoke is a no-op --
      // the chip's own icon has become the undo by then.
      const dup = p.some(
        s2 =>
          s2.change.op === 'revokeRepoAccess' &&
          s2.change.org === org &&
          s2.change.slug === team.slug &&
          s2.change.repo === repo,
      );
      if (dup) return p;
      idRef.current += 1;
      return [
        ...p,
        {
          id: idRef.current,
          label: `Revoke ${team.slug} access to ${repo}`,
          change: { op: 'revokeRepoAccess', org, slug: team.slug, repo },
        },
      ];
    });
  const onPermission = (team: Team, permission: string) =>
    stage(`Set ${team.slug} repo access → ${permission}`, {
      op: 'setPermission',
      org,
      slug: team.slug,
      permission,
    });
  const onEditConfig = (
    team: Team,
    patch: { description?: string; parentTeamId?: string; privacy?: string },
  ) => {
    const what = Object.keys(patch)[0] ?? 'details';
    stage(`Edit ${team.slug} (${what})`, {
      op: 'editConfig',
      org,
      slug: team.slug,
      ...patch,
    });
  };
  const onDelete = (team: Team) =>
    stage(`Delete team ${team.slug}`, { op: 'removeTeam', org, slug: team.slug });

  // Staged changes per org -- the rail's badge. The cart is global, so without
  // this a change parked in another org is invisible until the review dialog.
  const stagedByOrg = useMemo(() => {
    const out: Record<string, number> = {};
    for (const { change: c } of pendingChanges) {
      out[c.org] = (out[c.org] ?? 0) + 1;
    }
    return out;
  }, [pendingChanges]);

  // Rebuilt only when the queued count changes, not on every keystroke.
  const howItWorks = useMemo(
    () => howItWorksSteps(pendingChanges.length),
    [pendingChanges.length],
  );

  const orgStatList = useMemo(
    () =>
      data
        ? orgStats({
            orgs: data.orgs,
            order: ORG_ORDER,
            query: q,
            stagedByOrg,
          })
        : [],
    [data, q, stagedByOrg],
  );

  const teams = useMemo(() => data?.orgs[org] ?? [], [data, org]);
  const teamSlugs = useMemo(() => teams.map(t => t.slug), [teams]);
  // Every known login in the active org -- the add-member / new-team suggestions.
  const orgLogins = useMemo(
    () => [...new Set(teams.flatMap(t => t.members))].sort(),
    [teams],
  );
  // Every org owner visible in this org. Maintainer status is derived from the
  // single `*_org_owners` local, so anyone who maintains ANY team is an owner --
  // which is what makes the role org-wide rather than per team.
  const orgOwners = useMemo(
    () => new Set(teams.flatMap(t => t.maintainers)),
    [teams],
  );

  // Optimistic view of the cart for the active org so the tree shows staged
  // edits before any PR exists.
  const pending = useMemo<PendingState>(() => {
    const removedMembers = new Map<string, number>();
    const addedMembers = new Map<string, { login: string; id: number }[]>();
    const removedTeams = new Map<string, number>();
    const editedTeams = new Set<string>();
    const configEdits = new Map<string, ConfigOverlay>();
    const addedTeams = new Map<string, GhostTeam[]>();
    const grantEdits = new Map<
      string,
      { id: number; permission: string; repos: string[] }[]
    >();
    const grantRevokes = new Map<string, Map<string, number>>();
    for (const { id, change: c } of pendingChanges) {
      if (c.org !== org) continue;
      if (c.op === 'removeMember') {
        removedMembers.set(`${c.team}:${c.login}`, id);
      } else if (c.op === 'addMember') {
        const arr = addedMembers.get(c.team) ?? [];
        arr.push({ login: c.login, id });
        addedMembers.set(c.team, arr);
      } else if (c.op === 'removeTeam') {
        removedTeams.set(c.slug, id);
      } else if (c.op === 'grantRepoAccess') {
        // Badge + the pill tray's source of truth. Deliberately NOT folded into
        // configEdits.permission: that overlay feeds the "Default access (all
        // repos)" field, and a scoped grant is a different grant -- showing it
        // there would misstate the blast radius in exactly the way this
        // feature exists to end.
        editedTeams.add(c.slug);
        const arr = grantEdits.get(c.slug) ?? [];
        arr.push({ id, permission: c.permission, repos: c.repos });
        grantEdits.set(c.slug, arr);
      } else if (c.op === 'revokeRepoAccess') {
        editedTeams.add(c.slug);
        const m = grantRevokes.get(c.slug) ?? new Map<string, number>();
        m.set(c.repo, id);
        grantRevokes.set(c.slug, m);
      } else if (c.op === 'editConfig' || c.op === 'setPermission') {
        editedTeams.add(c.slug);
        // Merge rather than replace, and iterate in stage order, so two edits to
        // DIFFERENT fields both survive and two edits to the SAME field resolve
        // last-staged-wins -- which is the order the writer applies them in.
        const prev = configEdits.get(c.slug) ?? {};
        configEdits.set(
          c.slug,
          c.op === 'setPermission'
            ? { ...prev, permission: c.permission }
            : {
                ...prev,
                ...(c.description !== undefined ? { description: c.description } : {}),
                ...(c.parentTeamId !== undefined ? { parentTeamId: c.parentTeamId } : {}),
                ...(c.privacy !== undefined ? { privacy: c.privacy } : {}),
              },
        );
      } else if (c.op === 'addTeam') {
        const parent = c.parentTeamId ?? '';
        const arr = addedTeams.get(parent) ?? [];
        arr.push({
          id,
          slug: c.slug,
          description: c.description,
          parentTeamId: parent,
          members: c.members,
        });
        addedTeams.set(parent, arr);
      }
    }
    return {
      removedMembers,
      addedMembers,
      removedTeams,
      editedTeams,
      configEdits,
      addedTeams,
      grantEdits,
      grantRevokes,
      unstage,
    };
  }, [pendingChanges, org, unstage]);

  // Build the parent -> children forest. A team is a root when it has no parent
  // or its parent isn't in this org's file (orphan parent shown via breadcrumb).
  const { roots, childrenOf } = useMemo(() => {
    const slugSet = new Set(teams.map(t => t.slug));
    const byParent = new Map<string, Team[]>();
    const rootList: Team[] = [];
    for (const t of teams) {
      const parent =
        t.parentTeamId && slugSet.has(t.parentTeamId) ? t.parentTeamId : null;
      if (parent) {
        const arr = byParent.get(parent);
        if (arr) arr.push(t);
        else byParent.set(parent, [t]);
      } else {
        rootList.push(t);
      }
    }
    return { roots: rootList, childrenOf: byParent };
  }, [teams]);

  // Smart search: one box matches team slug/description AND member logins.
  // A subtree renders if the team or any descendant matches; matches force the
  // path open and member hits expand + highlight that team's people.
  const ql = q.trim().toLowerCase();
  const matchers = useMemo(() => {
    const selfMatch = (t: Team) =>
      !ql ||
      t.slug.toLowerCase().includes(ql) ||
      (t.description ?? '').toLowerCase().includes(ql) ||
      t.members.some(m => m.toLowerCase().includes(ql));
    const memberHit = (t: Team) =>
      Boolean(ql) && t.members.some(m => m.toLowerCase().includes(ql));
    const subtreeMatch = (t: Team): boolean =>
      selfMatch(t) ||
      (childrenOf.get(t.slug) ?? []).some(subtreeMatch);
    const shouldRender = (t: Team) => !ql || subtreeMatch(t);
    const forceOpen = (t: Team) =>
      Boolean(ql) && subtreeMatch(t) && !selfMatchOnly(t);
    // Open a node when the match is deeper than itself (so the path is visible),
    // or when one of its own members matched.
    function selfMatchOnly(t: Team) {
      const kids = childrenOf.get(t.slug) ?? [];
      return !memberHit(t) && !kids.some(subtreeMatch);
    }
    return { memberHit, shouldRender, forceOpen };
  }, [ql, childrenOf]);

  const renderTree = () => {
    if (teams.length === 0) {
      return (
        <Typography className={classes.empty}>No teams in this org.</Typography>
      );
    }
    const visibleRoots = roots.filter(matchers.shouldRender);
    // Staged root teams that match the query. Counted BEFORE the empty state:
    // searching for a team you just staged must find it, not claim nothing
    // matches -- the ghost is the only place that team exists yet.
    const matchingGhosts = (pending.addedTeams.get('') ?? []).filter(
      g =>
        !ql ||
        g.slug.toLowerCase().includes(ql) ||
        g.description.toLowerCase().includes(ql),
    );
    // An amend of a ROOT-level staged team belongs to the root form.
    const rootEdit =
      editTeam && editTeam.parentTeamId === '' ? editTeam : undefined;
    if (visibleRoots.length === 0 && matchingGhosts.length === 0) {
      return (
        <Typography className={classes.empty}>
          No teams or members in {org} match “{q.trim()}”.
        </Typography>
      );
    }
    // role="group" (not "tree"): a labelled generic container, which is what
    // this actually is now that the rows are disclosures.
    return (
      <div role="group" aria-label={`${org} teams`} className={classes.tree}>
        {visibleRoots.map(t => (
          <TeamNode
            key={t.slug}
            team={t}
            depth={0}
            childrenOf={childrenOf}
            isRoot
            teamSlugs={teamSlugs}
            orgLogins={orgLogins}
            orgOwners={orgOwners}
            org={org}
            query={ql}
            shouldRender={matchers.shouldRender}
            forceOpen={matchers.forceOpen}
            memberHit={matchers.memberHit}
            onMember={onMember}
            onEditConfig={onEditConfig}
            onPermission={onPermission}
            onGrantRepoAccess={onGrantRepoAccess}
            onRevokeRepoAccess={onRevokeRepoAccess}
            onDelete={onDelete}
            onStage={stage}
            editTeam={editTeam}
            onEditTeam={setEditTeam}
          />
        ))}
        {/* Staged ROOT teams, previewed in the tree. Filtered by the same
            query as real rows (slug/description are all a staged team has to
            match on), so a search never silently hides queued work. */}
        {matchingGhosts
          // The one being amended is represented by the open form instead, so
          // showing both would double the same staged team on screen.
          .filter(g => g.id !== editTeam?.id)
          .map(g => (
            <GhostTeamRow key={`ghost-${g.id}`} ghost={g} onEdit={setEditTeam} />
          ))}
        {(!ql || rootEdit) && (
          <NewTeamRow
            // Remount when the amend target changes, so the form picks up the
            // staged values as its initial state.
            key={rootEdit ? `edit-${rootEdit.id}` : 'new'}
            org={org}
            teamSlugs={teamSlugs}
            orgLogins={orgLogins}
            onStage={stage}
            editing={rootEdit}
            onEditDone={() => setEditTeam(null)}
          />
        )}
      </div>
    );
  };

  const body = () => {
    if (allowed === undefined) return <Progress />;
    if (allowed === false) {
      return (
        <InfoCard title="Team Admin">
          <Typography className={classes.empty}>
            You don't have access to team administration. It's limited to the
            platform admin and developer-productivity teams.
          </Typography>
        </InfoCard>
      );
    }
    if (failed) {
      return (
        <InfoCard title="Team Admin">
          <Typography className={classes.empty}>
            Couldn't load teams right now.
          </Typography>
        </InfoCard>
      );
    }
    if (!data) return <Progress />;

    return (
      <div className={classes.layout}>
        <div className={classes.mainCol}>
          <HowItWorks
            variant="plain"
            lead={
              <>
                GitHub teams across the m42 orgs, defined in{' '}
                <code>.github-private</code> Terraform. Edits here never touch
                GitHub directly — they're staged and shipped as one reviewed PR.
              </>
            }
            steps={howItWorks}
            collapseKey="m42.teamAdmin.howItWorks"
            summary={howItWorksSummary(pendingChanges.length)}
            footer={
              data.failedOrgs > 0 && (
                <Typography className={classes.usageWarn}>
                  {data.failedOrgs} org(s) failed to load.
                </Typography>
              )
            }
          />
          {/* Search sits ABOVE the org rail on purpose: it queries every org,
              so the query is the outer scope and the org the inner one. Below
              the rail it read as "filter within this org", which is the reading
              that let a person in another org look like no match at all. */}
          {/* A form, so Enter submits the way a search field is expected to.
              Filtering is live on every keystroke, so submit has nothing left
              to do -- preventDefault stops the browser navigating away and
              throwing the cart with it. Escape clears. */}
          <form
            className={classes.searchRow}
            role="search"
            onSubmit={e => e.preventDefault()}
          >
            <TextField
              className={classes.search}
              size="small"
              variant="outlined"
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') setQ('');
              }}
              placeholder="Search every org for a team or a person"
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    fontSize="small"
                    style={{ marginRight: 8, opacity: 0.6 }}
                  />
                ),
                // Only present when there is something to clear, so the field
                // is not carrying a permanently dead control.
                endAdornment: q ? (
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setQ('')}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : undefined,
              }}
              inputProps={{
                'aria-label': 'Search every org for a team or a person',
              }}
            />
            <div className={classes.searchEnd}>
              {loadedAt !== null && (
                <span className={classes.loadedAt}>
                  updated{' '}
                  {new Date(loadedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={reload}
                disabled={reloading}
              >
                {reloading ? 'Reloading…' : 'Reload'}
              </Button>
            </div>
          </form>
          <OrgSearchSummary
            stats={orgStatList}
            value={org}
            query={q}
            onChange={setOrg}
          />
          <Box mt={1.5}>
            <OrgRail
              stats={orgStatList}
              value={org}
              query={q}
              onChange={setOrg}
              panelId={ORG_PANEL_ID}
            />
          </Box>
          <Box mt={2}>
            <PendingContext.Provider value={pending}>
              <div role="tabpanel" id={ORG_PANEL_ID}>
                <InfoCard title={`${org} teams`}>{renderTree()}</InfoCard>
              </div>
            </PendingContext.Provider>
          </Box>
        </div>
        <aside className={classes.sideCol}>
          <AccessInfoCard groups={accessGroups} users={accessUsers} />
        </aside>
      </div>
    );
  };

  return (
    <Page themeId="tool">
      <Header
        title="Team Admin"
        subtitle="GitHub teams, managed via .github-private Terraform"
      />
      <Content>{body()}</Content>
      {/* The cart, as a PERSISTENT affordance.
          It used to be a button in a toolbar that appeared on the first edit
          (layout shift) and scrolled out of view exactly when you were editing
          deep in a 30-member tree -- the page's primary action was its least
          persistent element. Docked bottom-right it is always one click away,
          and it doubles as the count display so "how much have I staged" needs
          no scrolling either. */}
      {pendingChanges.length > 0 && (
        <Button
          className={classes.cartDock}
          color="primary"
          variant="contained"
          startIcon={<ShoppingCartIcon />}
          onClick={() => setReviewOpen(true)}
        >
          Review &amp; save ({pendingChanges.length})
        </Button>
      )}
      {/* The page's first live region: staging was previously silent to screen
          readers -- the ghost row is visual-only and the cart count lives
          elsewhere in the DOM. Polite, so it never interrupts typing. */}
      <div aria-live="polite" className={classes.srOnly}>
        {pendingChanges.length === 0
          ? 'No staged changes'
          : `${pendingChanges.length} staged change${
              pendingChanges.length === 1 ? '' : 's'
            }: ${pendingChanges[pendingChanges.length - 1].label}`}
      </div>
      <TeamReviewDialog
        open={reviewOpen}
        changes={pendingChanges}
        onRemove={id => setPendingChanges(p => p.filter(c => c.id !== id))}
        onClose={() => setReviewOpen(false)}
        onCommitted={() => {
          setPendingChanges([]);
          setReviewOpen(false);
          load();
        }}
      />
    </Page>
  );
};
